/**
 * Calendar View Feature Types
 *
 * These types define the data structures for the Calendar View feature.
 * They are designed to be compatible with the backend CalDavEvent model.
 */

import type { DateTime } from 'luxon';

import type { DraftTimelog, WeeklyWorkLog } from '@/types';

/**
 * Calendar event attendee with resolved display name.
 */
export type WorklogStatus = 'none' | 'maybe' | 'confirmed';

export interface EventAttendee {
  email: string;
  displayName: string | null;
  responseStatus: string | null;
  isKnownUser: boolean;
  worklogStatus: WorklogStatus;
  yandexTrackerUid: string | null;
}

/**
 * Calendar event as received from API (or mocked).
 * Mirrors backend CalDavEvent structure.
 */
export interface CalendarEvent {
  id: string;
  calendarId: string;
  externalId: string;
  recurrenceId: string | null;
  summary: string;
  description: string | null;
  startsAt: string; // ISO 8601 datetime
  endsAt: string; // ISO 8601 datetime
  isAllDay: boolean;
  location: string | null;
  worklogId: string | null;
  myResponseStatus: string | null;
  attendees: EventAttendee[];
}

/**
 * Calendar event with resolved calendar color.
 * Used for rendering in the UI and as API response type.
 */
export interface MatchedWorklog {
  /** Worklog duration in minutes */
  duration: number;
  /** Task key from Yandex Tracker (e.g. "PROJECT-123") */
  taskKey?: string;
  /** Worklog start time (ISO 8601) for positioning the fill within the event */
  start?: string;
  /** Worklog comment */
  comment?: string;
}

export interface CalendarEventWithColor extends CalendarEvent {
  color: string;
  matchedWorklog?: MatchedWorklog;
  /** True for non-calendar worklog pseudo-events */
  isOtherLog?: boolean;
  /** Original worklog data (for individual visible other logs) */
  otherLogData?: WeeklyWorkLog;
  /** Collapsed group data (for collapsed card pseudo-event) */
  collapsedGroup?: CollapsedLogGroup;
  /** True for draft timelog pseudo-events */
  isDraft?: boolean;
  /** Draft timelog data */
  draftData?: DraftTimelog;
}

/**
 * Event positioned for rendering in the time grid.
 * Includes layout information for overlapping events.
 */
export interface PositionedEvent {
  event: CalendarEventWithColor;
  /** Top position in pixels from the grid start */
  top: number;
  /** Height in pixels based on event duration */
  height: number;
  /** Left position as percentage (0-100) */
  left: number;
  /** Width as percentage (0-100) */
  width: number;
  /** Index within collision group (0-based) */
  columnIndex: number;
  /** Total columns in collision group */
  totalColumns: number;
}

/**
 * Data for a single day column in the week view.
 */
export interface DayColumn {
  /** The date of this day */
  date: DateTime;
  /** Events with specific times, positioned for rendering */
  timedEvents: PositionedEvent[];
  /** All-day events for this day */
  allDayEvents: CalendarEventWithColor[];
}

/**
 * Complete data for rendering the week view.
 */
export interface WeekViewData {
  /** Start of the week (Monday 00:00:00) */
  weekStart: DateTime;
  /** End of the week (Sunday 23:59:59) */
  weekEnd: DateTime;
  /** Array of 7 day columns (Monday to Sunday) */
  days: DayColumn[];
}

/**
 * Store state for the calendar view feature.
 */
export type CalendarViewStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'caldav_not_configured'
  | 'no_calendars_selected'
  | 'error';

/**
 * Props for EventBlock component.
 */
export interface EventBlockProps {
  event: PositionedEvent;
  onClick: (event: CalendarEventWithColor) => void;
}

/**
 * Props for EventDetailsModal component.
 */
export interface EventDetailsModalProps {
  open: boolean;
  onClose: () => void;
  event: CalendarEventWithColor | null;
  /** Attendees loaded lazily; overrides event.attendees when provided */
  attendees?: EventAttendee[];
  /** True while attendees are being fetched */
  attendeesLoading?: boolean;
  onLogTime?: (event: CalendarEventWithColor, forAll: boolean) => void;
  onEditWorklog?: (event: CalendarEventWithColor) => void;
  onDeleteWorklog?: (event: CalendarEventWithColor) => void | Promise<void>;
  /** Только просмотр: без кнопок «Залогать», «Редактировать» и «Удалить» */
  readOnly?: boolean;
}

/**
 * Props for CalDavRequiredOverlay component.
 */
export interface CalDavRequiredOverlayProps {
  onSettingsClick: () => void;
}

/**
 * Props for TimeGrid component.
 */
export interface TimeGridProps {
  days: DayColumn[];
  onEventClick: (event: CalendarEventWithColor) => void;
  /** Hour to scroll to on mount (default: 8) */
  initialScrollHour?: number;
  /** Показывать кнопку «Создать черновик» в попапе выделения (только при просмотре себя) */
  showCreateDraftOption?: boolean;
  /** Только просмотр: выделение времени мышью (создание лога или черновика) выключено */
  readOnly?: boolean;
}

/**
 * Props for AllDaySection component.
 */
export interface AllDaySectionProps {
  days: DayColumn[];
  onEventClick: (event: CalendarEventWithColor) => void;
}

/**
 * Constants for the calendar view.
 */
/**
 * Defaults for pre-filling the time log form from a calendar event.
 */
export interface LogFormDefaults {
  startDate: string;
  startTime: string;
  duration: string;
  comment: string;
  taskKey: string | null;
  attendees?: EventAttendee[];
}

/**
 * Group of worklogs in the same time slot, with collapsing logic applied.
 */
export interface CollapsedLogGroup {
  /** All logs in this time slot */
  logs: WeeklyWorkLog[];
  /** Logs shown as individual blocks (1-2 max) */
  visibleLogs: WeeklyWorkLog[];
  /** Logs hidden behind the collapsed card */
  collapsedLogs: WeeklyWorkLog[];
  /** Min duration among collapsed logs (determines collapsed card height) */
  minCollapsedDuration: number;
  /** Total duration of all collapsed logs */
  totalCollapsedDuration: number;
  /** ISO date string (YYYY-MM-DD) */
  date: string;
  /** Minutes from start of day for positioning */
  startMinutes: number;
}

/**
 * Props for OtherLogsDetailPanel component.
 */
export interface OtherLogsDetailPanelProps {
  open: boolean;
  group: CollapsedLogGroup | null;
  onClose: () => void;
  onEditLog: (log: WeeklyWorkLog) => void;
}

export const CALENDAR_VIEW_CONSTANTS = {
  /** Height of one hour in the time grid (pixels) */
  HOUR_HEIGHT: 80,
  /** Minimum event block height (pixels) */
  MIN_EVENT_HEIGHT: 20,
  /** Default hour to scroll to on page load */
  DEFAULT_SCROLL_HOUR: 8,
  /** Hours displayed in the time grid */
  HOURS_IN_DAY: 24,
  /** Days in a week */
  DAYS_IN_WEEK: 7,
  /** Resolution in minutes for day status bar segments */
  STATUS_BAR_RESOLUTION_MINUTES: 1,
} as const;

/** Status of a time interval for the day column status bar */
export type DaySegmentStatus = 'free' | 'logged' | 'unlogged_meeting';

export interface DayStatusSegment {
  startMinutes: number;
  endMinutes: number;
  status: DaySegmentStatus;
}
